import React from "react";
import { Col, Row, Modal, Button, Form, Container } from 'react-bootstrap';
import { Formik } from "formik";
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

const ModalContents = Object.freeze({
  Login: 0,
  Signup: 1,
  ForgotPassword: 2,
});

const loginValidationSchema = yup.object().shape(
  {
    email: yup.string().email().required("Email is required"),
    password: yup.string().required("Password is required")
  }
);

const signupValidationSchema = yup.object().shape(
  {
    firstName: yup.string().required("First Name is required."),
    lastName: yup.string().required("Last Name is required"),
    email: yup.string().email().required("Email is required"),
    password: yup.string().required("Password is required").matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/),
    confirmPassword: yup.string().required("Passwords must match").oneOf([yup.ref("password")], "Passwords must match"),
  }
);

const forgotPasswordValidationSchema = yup.object().shape(
  {
    email: yup.string().email().required("Email is required.")
  }
);

const getPasswordValidationErrorMessage = (error) => {
  if (error) {
    if (error.includes("match")) {
      return "password must be at least 8 characters long and include a lowercase letter, uppercase letter, digit, and special character"
    }

    return error
  }

  return undefined;
}

const LoginModal = ({ show, onHide }) => {
  const { login, signup } = useAuth();
  const [modalContents, setModalContents] = React.useState(ModalContents.Login);
  const [loginError, setLoginError] = React.useState('');

  const loginModalContents = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Login to your account.</Modal.Title>
      </Modal.Header>
      <Container hidden={loginError === ''} style={{ placeItems: 'center', backgroundColor: 'red', color: 'white'}}>
        <p>{loginError}</p>
      </Container>
      <Modal.Body>
        <Formik
          validationSchema={loginValidationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await login(values.email, values.password);
              setLoginError('');
              onHide();
            }
            catch (error) {
              console.error("Submission Error:", error.message);
              setLoginError(error.message);
            }
            finally {
              setSubmitting(false);
            }
          }}
          initialValues={{
            email: '',
            password: '',
          }}
        >
          {({ handleSubmit, handleChange, isSubmitting, values, touched, errors }) =>
          (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className="mb-3"
                controlId="formBasicPassword"
              >
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password} />
                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                <div className="d-flex justify-content-end">
                  <Button style={{ fontSize: "1em" }} variant="link" onClick={() => { setModalContents(ModalContents.ForgotPassword) }}>Forgot Password?</Button>
                </div>
              </Form.Group>
              <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={isSubmitting}>Login</Button>
              </div>
            </Form>)}
        </Formik>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column align-items-center w-100">
        <p className="mb-2">- Don't have an account? -</p>
        <Button variant="primary" onClick={() => setModalContents(ModalContents.Signup)} className="w-100">
          Create an account
        </Button>
      </Modal.Footer>
    </>
  )

  const signupModalContents = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Create an account.</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          validationSchema={signupValidationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await signup(values.email, values.password, values.firstName, values.lastName);
            }
            catch (error) {
              console.error("Submission Error:", error);
              // TODO: Display Error to User
            }
            finally {
              setSubmitting(false);
              onHide();
            }
          }}
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            password: '',
          }}
        >
          {({ handleSubmit, handleChange, isSubmitting, values, errors }) =>
          (
            <Form noValidate onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <Form.Group className="mb-3" controlId="formBasicFirstName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      isInvalid={!!errors.firstName}
                    />
                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3" controlId="formBasicLastName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      isInvalid={!!errors.lastName}
                    />
                    <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className="mb-3"
                controlId="formBasicPassword"
              >
                <Form.Label>Create Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password} />
                <Form.Control.Feedback type="invalid">{getPasswordValidationErrorMessage(errors.password)}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className="mb-3"
                controlId="formBasicPasswordConfirm"
              >
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  isInvalid={!!errors.confirmPassword} />
                <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
              </Form.Group>
              <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={isSubmitting}>Create Account</Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column align-items-center w-100">
        <p className="mb-2">- Already have an account? -</p>
        <Button variant="primary" onClick={() => setModalContents(ModalContents.Login)} className="w-100">
          Login with existing account
        </Button>
      </Modal.Footer>
    </>
  )

  const forgotPasswordModalContents = () => (
    <>
      <Formik
        validationSchema={forgotPasswordValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await this.sendForgotPasswordRequest(values.email);
          }
          catch (error) {
            console.error("Submission Error:", error);
            // TODO: Display Error to User
          }
          finally {
            setSubmitting(false);
            onHide();
          }
        }}
        initialValues={{
          email: '',
        }}
      >
        {({ handleSubmit, handleChange, isSubmitting, values, errors }) =>
        (<>
          <Modal.Header className="d-flex flex-column align-items-start">
            <div className="d-flex w-100 justify-content-between align-items-center">
              <Modal.Title>Forgot your password?</Modal.Title>
              <button
                type="button"
                className="btn-close "
                onClick={() => { onHide(); setModalContents(ModalContents.Login); }}
              ></button>
            </div>
            <Form.Text>
              Enter your email address and we will send you instructions to reset your password.
            </Form.Text>
          </Modal.Header>
          <Modal.Body>
            <Form id="resetPasswordForm" noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalContents(ModalContents.Login)}>
              Go Back
            </Button>
            <Button variant="primary" type="submit" form="resetPasswordForm" disabled={isSubmitting}>
              Continue
            </Button>
          </Modal.Footer></>)}
      </Formik>
    </>
  )

  const sendForgotPasswordRequest = async () => {
    await Promise.resolve();
  }

  return (
    <Modal size="md" centered style={{ color: "black" }} show={show} onHide={() => { setModalContents(ModalContents.Login); onHide(); }}>
      {(() => {
        switch (modalContents) {
          case ModalContents.Signup:
            return signupModalContents();
          case ModalContents.ForgotPassword:
            return forgotPasswordModalContents();
          default:
            return loginModalContents();
        }
      })()}
    </Modal>
  );
};

export default LoginModal;